// Option values are prefixed so that a sponsor id can never reach the request
// body as `companyId`.

export interface PickerCompany {
  id: string;
  name: string;
}

/** A row of `GET /events/:eventId/sponsors/admin`; only these fields are read. */
export interface PickerSponsor {
  id: string;
  name: string;
  companyId: string | null;
}

export interface PickFields {
  organizationName?: string;
  attendeeType?: string;
}

/**
 * The picker's form value. A sponsor pick remembers what it wrote and what
 * stood there before, so that taking it back touches nothing the organizer
 * has edited since.
 */
export type CompanyPick =
  | { kind: 'company'; companyId: string }
  | {
      kind: 'sponsor';
      sponsorId: string;
      companyId: string | null;
      filledName: string;
      before: PickFields;
    };

export type PickerChoice =
  | { kind: 'company'; companyId: string }
  | { kind: 'sponsor'; sponsor: PickerSponsor };

const COMPANY_PREFIX = 'company:';
const SPONSOR_PREFIX = 'sponsor:';

export const companyOptionValue = (id: string) => `${COMPANY_PREFIX}${id}`;
export const sponsorOptionValue = (id: string) => `${SPONSOR_PREFIX}${id}`;

export function optionValueOf(pick: CompanyPick | undefined): string | undefined {
  if (!pick) return undefined;
  return pick.kind === 'company'
    ? companyOptionValue(pick.companyId)
    : sponsorOptionValue(pick.sponsorId);
}

export function choiceOf(
  value: string | undefined,
  sponsors: PickerSponsor[]
): PickerChoice | undefined {
  if (value?.startsWith(COMPANY_PREFIX)) {
    return { kind: 'company', companyId: value.slice(COMPANY_PREFIX.length) };
  }
  if (value?.startsWith(SPONSOR_PREFIX)) {
    const id = value.slice(SPONSOR_PREFIX.length);
    const sponsor = sponsors.find((s) => s.id === id);
    return sponsor ? { kind: 'sponsor', sponsor } : undefined;
  }
  return undefined;
}

/**
 * Takes back the previous pick, then applies the next one, in that order: a
 * sponsor-to-sponsor switch must remember the fields as they were before the
 * first sponsor, not the first sponsor's fill.
 */
export function applyPick(
  current: PickFields,
  previous: CompanyPick | undefined,
  choice: PickerChoice | undefined
): { fields: PickFields; pick: CompanyPick | undefined } {
  const fields: PickFields = { ...current };
  if (previous?.kind === 'sponsor') {
    if (fields.organizationName === previous.filledName) {
      fields.organizationName = previous.before.organizationName ?? '';
    }
    if (fields.attendeeType === 'SPONSOR') {
      fields.attendeeType = previous.before.attendeeType ?? 'PARTICIPANT';
    }
  }

  if (!choice) return { fields, pick: undefined };
  if (choice.kind === 'company') {
    return { fields, pick: { kind: 'company', companyId: choice.companyId } };
  }
  const { sponsor } = choice;
  return {
    fields: { ...fields, organizationName: sponsor.name, attendeeType: 'SPONSOR' },
    pick: {
      kind: 'sponsor',
      sponsorId: sponsor.id,
      companyId: sponsor.companyId,
      filledName: sponsor.name,
      before: fields,
    },
  };
}
